'use client';

import ArticlePagination from '@/components/articlePagination/ArticlePagination';
import ArticleStage from '@/components/articleStage/ArticleStage';
import { Button } from '@/components/button';
import ContactCardsComponent from '@/components/contactCards/ContactCards';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import Pill from '@/components/pill/Pill';
import { RichText } from '@/components/richText/RichText';
import { getFormattedDate } from '@/helper/dates';
import { PRESS_SLUG_SLASH } from '@/helper/press';
import { resolveStory } from '@/helper/utils';
import type { PageMagArticleProps } from '@/storyblok/PageMagArticle';
import type {
  EntityContactStoryblok,
  GlobalCompanyStoryblok,
  PagePressArticleStoryblok,
  RichtextStoryblok,
} from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type PagePressArticleProps = Omit<PageMagArticleProps, 'story' | 'blok'> & {
  story: ISbStoryData<PagePressArticleStoryblok>;
  blok?: PagePressArticleStoryblok;
};

export const PagePressArticle: React.FC<PagePressArticleProps> = (props) => {
  const { t } = useTranslation();

  const contacts: ISbStoryData<EntityContactStoryblok>[] = [];
  let companyText: RichtextStoryblok | null = null;
  let contentHeadline: ReactNode | null = null;
  let contactHeadlineFieldContent = t('press.contact.title');

  const contact = resolveStory<EntityContactStoryblok>(props.blok?.contact);
  const company = resolveStory<GlobalCompanyStoryblok>(props.blok?.company);

  if (contact) {
    contacts.push(contact);
    if (contact.content.pronouns === 'f') {
      contactHeadlineFieldContent = t('press.contact.titleF');
    } else if (contact.content.pronouns === 'm') {
      contactHeadlineFieldContent = t('press.contact.titleM');
    }
  }

  if (company && company.content.text) {
    companyText = company.content.text;

    contentHeadline = (
      <HeadlineField tag={HeadlineTag.H3} style={HeadlineTag.H5}>
        {t('press.company.about')} {company.content.name}
      </HeadlineField>
    );
  }

  const contactHeadlineField = (
    <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
      {contactHeadlineFieldContent}
    </HeadlineField>
  );
  const date = getFormattedDate(props.firstPublishedAt);

  const stageFooter = <span>{date}</span>;
  const stagePills = company && <Pill>{company.name}</Pill>;

  return (
    <div data-testid="press-article-page">
      {props.blok && (
        <ArticleStage
          linkLastElementInBreadcrumb={true}
          breadcrumbs={props.breadcrumbs}
          article={props.blok}
          footerContent={stageFooter}
          pills={stagePills}
        />
      )}
      <div className="grid-wrapper">
        <div className="content-bloks-wrapper">
          <section className="article-summary">
            <RichText text={props.blok?.introduction} />
          </section>

          <NestedBlocks blocks={props.blok?.content} />

          {contacts && companyText && (
            <ContactCardsComponent
              headline={contactHeadlineField}
              contentHeadline={contentHeadline}
              contacts={contacts}
              content={companyText}
            />
          )}

          {props.relatedPressArticles && props.relatedPressArticles.length > 0 && (
            <section className="article-more">
              <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
                {t('press.relatedArticles')}
              </HeadlineField>
              <ArticlePagination baseUrl={`/${PRESS_SLUG_SLASH}`} articles={props.relatedPressArticles} />
              <Button
                className="backToViewButton"
                data-testid="press-article-page-back-to-overview-button"
                link={props.breadcrumbs[1] && props.breadcrumbs[1].full_slug}
              >
                {t('backToOverview')}
              </Button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
export default PagePressArticle;
